/**
 * Node Executors
 *
 * Maps node types to their executor implementations.
 * Each executor handles the API calls for a specific node type.
 *
 * Pattern:
 * - POST to start job → returns job_id
 * - GET /jobs/{job_id}/status → poll until complete
 * - GET /results/{job_id} → retrieve results
 */

import * as ingestExecutor from './ingestExecutor'
import * as codeAnalysisExecutor from './codeAnalysisExecutor'
import * as aiNamingExecutor from './aiNamingExecutor'
import * as codeRefactorExecutor from './codeRefactorExecutor'
import * as dependencyMapperExecutor from './dependencyMapperExecutor'
import * as monolithIdentifierExecutor from './monolithIdentifierExecutor'
import * as dataAnalysisExecutor from './dataAnalysisExecutor'
import * as discoveryExecutor from './discoveryExecutor'
import * as architectureExecutor from './architectureExecutor'
import * as javaPackagingExecutor from './javaPackagingExecutor'
import * as javaValidationExecutor from './javaValidationExecutor'
import * as preFlightExecutor from './preFlightExecutor'
import * as serviceGenerationExecutor from './serviceGenerationExecutor'
import * as javaAnalysisExecutor from './javaAnalysisExecutor'
import * as businessLogicExecutor from './businessLogicExecutor'
import * as codeTranslationExecutor from './codeTranslationExecutor'
import * as idmsAnalysisExecutor from './idmsAnalysisExecutor'
import * as idmsCompatibilityExecutor from './idmsCompatibilityExecutor'
import * as idmsDataArchitectureExecutor from './idmsDataArchitectureExecutor'
import * as idmsAssessmentExecutor from './idmsAssessmentExecutor'
import * as idmsSqlGenerationExecutor from './idmsSqlGenerationExecutor'
import * as cobolVersionUpgradeExecutor from './cobolVersionUpgradeExecutor'
import * as codeRefactorV2Executor from './codeRefactorV2Executor'
import { testStubsExecutor, smartTestsExecutor } from './testGenerationExecutor'
import {
  waitExecutor,
  conditionExecutor,
  loopExecutor,
  parallelExecutor,
  checkpointExecutor,
  notifyExecutor,
  customScriptExecutor,
} from './controlExecutors'
import {
  aiTransformExecutor,
  aiExplainExecutor,
  aiReviewExecutor,
  aiChatExecutor,
} from './aiExecutors'

/**
 * Map of node types to their executors
 */
export const NODE_EXECUTORS = {
  // Ingest nodes (both category and pipeline versions)
  'upload-files': ingestExecutor,
  'pipeline-upload': ingestExecutor,

  // Code Analysis nodes (both category and pipeline versions)
  'code-analysis': codeAnalysisExecutor,
  'pipeline-code-analysis': codeAnalysisExecutor,

  // AI Naming nodes (both category and pipeline versions)
  'ai-naming': aiNamingExecutor,
  'pipeline-ai-naming': aiNamingExecutor,

  // Code Refactor nodes (both category and pipeline versions)
  'code-refactor': codeRefactorExecutor,
  'pipeline-refactor': codeRefactorExecutor,

  // Dependency Mapper nodes (both category and pipeline versions)
  'dependency-mapper': dependencyMapperExecutor,
  'pipeline-dependencies': dependencyMapperExecutor,

  // Monolith Identifier nodes (both category and pipeline versions)
  'monolith-identifier': monolithIdentifierExecutor,
  'pipeline-monolith': monolithIdentifierExecutor,

  // Data Analysis nodes (both category and pipeline versions)
  'data-analysis': dataAnalysisExecutor,
  'pipeline-data': dataAnalysisExecutor,

  // Discovery nodes (both category and pipeline versions)
  'discovery-roi': discoveryExecutor,
  'pipeline-discovery': discoveryExecutor,

  // Architecture nodes (both category and pipeline versions)
  'architecture': architectureExecutor,
  'pipeline-architecture': architectureExecutor,

  // Java Validation nodes
  'java-validation': javaValidationExecutor,
  'pipeline-java-validation': javaValidationExecutor,

  // Pre-Flight compile check nodes
  'pre-flight': preFlightExecutor,
  'pipeline-pre-flight': preFlightExecutor,

  // Java Packaging nodes
  'generate-java-package': javaPackagingExecutor,
  'pipeline-java-package': javaPackagingExecutor,

  // Business Logic Analysis nodes (both category and pipeline versions)
  'business-logic': businessLogicExecutor,
  'pipeline-business-logic': businessLogicExecutor,

  // Code Translation nodes (both category and pipeline versions)
  'code-translation': codeTranslationExecutor,
  'pipeline-code-translation': codeTranslationExecutor,

  // Service Generation nodes
  'service-generation': serviceGenerationExecutor,
  'pipeline-service-gen': serviceGenerationExecutor,

  // Java Analysis nodes (both category and pipeline versions)
  'java-analysis': javaAnalysisExecutor,
  'pipeline-java-analysis': javaAnalysisExecutor,

  // IDMS Analysis nodes (both category and pipeline versions)
  'idms-analysis': idmsAnalysisExecutor,
  'pipeline-idms-analysis': idmsAnalysisExecutor,

  // IDMS Compatibility nodes (both category and pipeline versions)
  'idms-compatibility': idmsCompatibilityExecutor,
  'pipeline-idms-compatibility': idmsCompatibilityExecutor,

  // IDMS Data Architecture nodes (both category and pipeline versions)
  'idms-data-architecture': idmsDataArchitectureExecutor,
  'pipeline-idms-data-architecture': idmsDataArchitectureExecutor,

  // IDMS Assessment nodes (both category and pipeline versions)
  'idms-assessment': idmsAssessmentExecutor,
  'pipeline-idms-assessment': idmsAssessmentExecutor,

  // IDMS SQL Generation nodes (both category and pipeline versions)
  'idms-sql-generation': idmsSqlGenerationExecutor,
  'pipeline-idms-sql-generation': idmsSqlGenerationExecutor,

  // COBOL Version Upgrade nodes (both category and pipeline versions)
  'cobol-version-upgrade': cobolVersionUpgradeExecutor,
  'pipeline-cobol-version-upgrade': cobolVersionUpgradeExecutor,

  // Code Refactor v2 nodes (both category and pipeline versions)
  'code-refactor-v2': codeRefactorV2Executor,
  'pipeline-code-refactor-v2': codeRefactorV2Executor,

  // Test Generation nodes
  'generate-test-stubs': testStubsExecutor,
  'generate-smart-tests': smartTestsExecutor,
  'pipeline-smart-tests': smartTestsExecutor,

  // Control nodes - workflow control and utilities
  'wait': waitExecutor,
  'condition': conditionExecutor,
  'loop': loopExecutor,
  'parallel': parallelExecutor,
  'checkpoint': checkpointExecutor,
  'notify': notifyExecutor,
  'custom-script': customScriptExecutor,

  // AI nodes - AI-powered analysis and transformation
  'ai-transform': aiTransformExecutor,
  'ai-explain': aiExplainExecutor,
  'ai-review': aiReviewExecutor,
  'ai-chat': aiChatExecutor,
}

// Re-export auth helper (defined in separate file to avoid circular imports)
export { getAuthHeaders } from './authHelper.js'

/**
 * Check if a node type has an executor
 */
export function hasExecutor(nodeType) {
  return nodeType in NODE_EXECUTORS
}

/**
 * Get the executor for a node type
 */
export function getExecutor(nodeType) {
  return NODE_EXECUTORS[nodeType] || null
}

export default NODE_EXECUTORS
